import FontIcon from '@luminopia/react-toolbox/lib/font_icon'
import {List, ListItem} from '@luminopia/react-toolbox/lib/list'
import ProgressBar from '@luminopia/react-toolbox/lib/progress_bar'
import {Expandable} from 'core/components/Expandable'
import {PrescriptionHistory} from 'core/components/prescription/prescription-history'

import * as React from 'react'
import {connect, Dispatch} from 'react-redux'
import {RouteComponentProps} from 'react-router'

import {IPrescriptionData} from 'core/api/patient'
import {DosageChart} from 'core/components/prescription/dosage-chart'
import {DosageTable} from 'core/components/prescription/dosage-table'
import Card from 'study_coordinator/components/Card'
import {SubjectControls, SubjectSummary} from 'study_coordinator/components/subjects'
import * as routes from 'study_coordinator/routes'
import {
  IState,
  selectSubject,
  selectSubjectPrescriptions,
  selectSubjectTreatmentData,
  selectSubjectTreatmentDataDateAsc,
} from 'study_coordinator/store'
import {
  deactivateSubjectRequest,
  reactivateSubjectRequest,
  getSubjectsRequest,
  getSubjectTreatmentReportRequest,
  model as SubjectsModel,
  resetIsSubjectActivationConfirmActive,
  selectSubjectLastActivatedPrescription,
  setIsSubjectActivationConfirmActive,
} from 'study_coordinator/store/subjects'
import * as classNames from './SubjectPage.css'

export interface IRouteParams {
  subjectIdentifier: string
}

interface IStateProps {
  subject?: SubjectsModel.ISubject
  dailyTreatmentData?: SubjectsModel.TreatmentData | null
  dailyTreatmentDataAsc?: SubjectsModel.TreatmentData | null
  prescriptions?: IPrescriptionData[] | null
  lastPrescription?: IPrescriptionData | null
  isFetching: boolean
  isConfirmActive: boolean
}
interface IDispatchProps {
  onMount?: () => void
  onConfirmActivate: () => void
  onConfirmDeactivate: () => void
  onDeactivate: ({ reason }: { reason: string }) => void
  onReactivate: () => void
}
interface IOwnProps extends RouteComponentProps<IRouteParams> {}
export interface IProps extends IStateProps, IDispatchProps, IOwnProps {}

type ComponentState = {
  isUsageExpanded: boolean,
}

export class SubjectPage extends React.Component<IProps, ComponentState> {
  public state = {
    isUsageExpanded: false,
  }

  // TODO I18N
  public render() {
    // TODO DRY this functionality with a HOC
    if (this.props.isFetching) {
      return <ProgressBar type='circular' mode='indeterminate' multicolor />
    } else if (!this.props.subject) {
      return <h1>Patient not found</h1>
    }
    const {
      subjectIdentifier,
      accessCode,
      isTreatmentActive,
      partnerOrderId,
    } = this.props.subject
    const {prescriptions} = this.props

    const hasArchivedPrescription = prescriptions
      ? prescriptions.some(prescription => prescription.archivedAt !== null)
      : false

    return (
      <div>
        <Card title={`Patient ${subjectIdentifier}`} subtitle='summary' icon='person_outline'>
          <div className={classNames.headerContainer}>
            <div>
              <SubjectSummary
                subjectIdentifier={subjectIdentifier}
                isTreatmentActive={isTreatmentActive}
                isTreatmentArchived={hasArchivedPrescription}
              />
              <List>
                <ListItem
                  legend='Patient ID'
                  caption={subjectIdentifier}
                  leftIcon='person'
                  ripple={false}
                />
                {/* <ListItem
                legend='phil patient id'
                caption={partnerOrderId || 'N/A'}
                leftIcon='medication'
                ripple={false}
              /> */}
                <ListItem
                  legend='Access Code'
                  caption={accessCode}
                  leftIcon='fingerprint'
                  ripple={false}
                />
              </List>
            </div>
            <SubjectControls
              deactivateButton={isTreatmentActive && !hasArchivedPrescription}
              reactivateButton={prescriptions ? prescriptions.length > 0 && !isTreatmentActive && !hasArchivedPrescription : false}
              isConfirmActive={this.props.isConfirmActive}
              onConfirmActivate={this.props.onConfirmActivate}
              onConfirmDeactivate={this.props.onConfirmDeactivate}
              onDeactivate={this.props.onDeactivate}
              onReactivate={this.props.onReactivate}
              updatePrescriptionRoute={routes.prescriptionUpdateRoute(subjectIdentifier)}
            />
          </div>
        </Card>

        <Card title='Prescription' subtitle={`Patient ${subjectIdentifier}`} icon='assignment'>
          <PrescriptionHistory prescriptions={prescriptions || []}/>
        </Card>

        {this.recentUsageCard()}

        {this.usageCard()}
      </div>
    )
  }

  public componentDidMount() {
    if (this.props.onMount) this.props.onMount()
  }

  private recentUsageCard() {
    const {dailyTreatmentDataAsc, subject, lastPrescription} = this.props
    if (!subject || !dailyTreatmentDataAsc || !lastPrescription) return null

    const referenceValue = lastPrescription.dailyDosage

    return dailyTreatmentDataAsc.dailyTreatmentData.length > 0 &&
      <Card title='Recent Usage' subtitle={`Patient ${subject.subjectIdentifier}`} icon='show_chart'>
        <DosageChart
          width='100%'
          height={300}
          minWidth={300}
          referenceValue={referenceValue}
          data={dailyTreatmentDataAsc.dailyTreatmentData}
        />
      </Card>
  }

  private usageCard() {
    const {dailyTreatmentData, subject} = this.props
    if (!subject || !dailyTreatmentData) return null

    return (
      <Card title='Usage' subtitle={`Patient ${subject.subjectIdentifier}`} icon='list'>
        <Expandable
          handle={<FontIcon value={this.state.isUsageExpanded ? 'expand_less' : 'expand_more'} />}
          isOpen={this.state.isUsageExpanded}
          onClickHandle={this.handleClickUsageCardHandle}
        >
          <DosageTable
            data={dailyTreatmentData.dailyTreatmentData}
            totalDosage={dailyTreatmentData.totalRecommendedDosage}
            totalCompletedDosage={dailyTreatmentData.totalCompletedDosage}
          />
        </Expandable>
      </Card>
    )
  }

  private handleClickUsageCardHandle = () => this.setState({isUsageExpanded: !this.state.isUsageExpanded})
}

const mapStateToProps = (state: IState, ownProps: IOwnProps): IStateProps => {
  const subjectIdentifier = ownProps.match.params.subjectIdentifier
  const subject = selectSubject(state, subjectIdentifier)
  const isFetching = state.subjects.isFetching && state.subjects.treatmentReport.isFetching

  if (!subject) {
    return {
      isFetching,
      isConfirmActive: state.subjects.isConfirmActive,
    }
  }

  const prescriptions = selectSubjectPrescriptions(state, subjectIdentifier)
  const lastPrescription = selectSubjectLastActivatedPrescription(state, subjectIdentifier)
  const dailyTreatmentData = selectSubjectTreatmentData(state, subjectIdentifier)
  const dailyTreatmentDataAsc = selectSubjectTreatmentDataDateAsc(state, subjectIdentifier)
  return {
    subject,
    isFetching,
    isConfirmActive: state.subjects.isConfirmActive,
    prescriptions,
    lastPrescription,
    dailyTreatmentData,
    dailyTreatmentDataAsc,
  }
}

const mapDispatchToProps = (dispatch: Dispatch<void>, ownProps: IOwnProps): IDispatchProps => ({
  onMount: () => {
    dispatch(getSubjectsRequest())
    dispatch(getSubjectTreatmentReportRequest(ownProps.match.params.subjectIdentifier))
  },
  onConfirmActivate: () => dispatch(setIsSubjectActivationConfirmActive()),
  onConfirmDeactivate: () => dispatch(resetIsSubjectActivationConfirmActive()),
  onDeactivate: ({ reason }: { reason: string }) => dispatch(deactivateSubjectRequest({
    subjectIdentifier: ownProps.match.params.subjectIdentifier,
    reason
  })),
  onReactivate: () => dispatch(reactivateSubjectRequest({
    subjectIdentifier: ownProps.match.params.subjectIdentifier,
  })),
})

export default connect<IStateProps, IDispatchProps, IOwnProps, IState>(
  mapStateToProps,
  mapDispatchToProps,
)(SubjectPage)



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./study_coordinator/src/pages/SubjectPage.tsx